import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/ErrorBoundary";
import { logPageView } from "./utils/analytics";

// Code splitting: Lazy load all page components
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const GetInTouch = lazy(() => import("./pages/GetInTouch"));
const AssetFinance = lazy(() => import("./pages/AssetFinance"));
const VehicleFinance = lazy(() => import("./pages/VehicleFinance"));
const BusinessLoan = lazy(() => import("./pages/BusinessLoan"));
const PrivacyNotice = lazy(() => import("./pages/PrivacyNotice"));
const ComplaintsProcedure = lazy(() => import("./pages/ComplaintsProcedure"));
const NotFound = lazy(() => import("./pages/NotFound"));

function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathnameRef = useRef();

  useEffect(() => {
    // Only scroll if pathname actually changed
    if (prevPathnameRef.current !== undefined && prevPathnameRef.current !== pathname) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      // Track page view in Google Analytics
      logPageView(pathname);
    }
    prevPathnameRef.current = pathname;
  }, [pathname]);

  return null;
}

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    if (window.location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="container mx-auto px-6 py-5">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex-shrink-0"
                onClick={(e) => {
                  const { pathname } = window.location;
                  if (pathname === '/') {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                  setMobileMenuOpen(false);
                }}
              >
                {logoError ? (
                  <span className="text-2xl font-bold text-blue-900">Finaways</span>
                ) : (
                  <img
                    src="/finawaysLogo.png"
                    alt="Finaways - Asset Funding Experts"
                    className="h-14"
                    width="auto"
                    height="56"
                    loading="eager"
                    fetchpriority="high"
                    onError={() => setLogoError(true)}
                  />
                )}
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-6">
                <Link
                  to="/"
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium text-base"
                  onClick={(e) => {
                    if (window.location.pathname === '/') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium text-base"
                  onClick={(e) => {
                    if (window.location.pathname === '/about') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  About
                </Link>
                <Link
                  to="/asset-finance"
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium text-base"
                  onClick={(e) => {
                    if (window.location.pathname === '/asset-finance') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  Asset Finance
                </Link>
                <Link
                  to="/vehicle-finance"
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium text-base"
                  onClick={(e) => {
                    if (window.location.pathname === '/vehicle-finance') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  Vehicle Finance
                </Link>
                <Link
                  to="/business-loan"
                  className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium text-base"
                  onClick={(e) => {
                    if (window.location.pathname === '/business-loan') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  Business Loans
                </Link>
                <Link
                  to="/get-in-touch"
                  className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium text-base"
                  onClick={(e) => {
                    if (window.location.pathname === '/get-in-touch') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  Get in Touch
                </Link>
              </nav>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden text-gray-700 hover:text-blue-600 focus:outline-none"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <nav className="md:hidden mt-4 pb-4 border-t pt-4">
                <div className="flex flex-col gap-2">
                  <Link
                    to="/"
                    className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                    onClick={(e) => {
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      handleNavClick('/');
                    }}
                  >
                    Home
                  </Link>
                  <Link
                    to="/about"
                    className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                    onClick={(e) => {
                      if (window.location.pathname === '/about') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      handleNavClick('/about');
                    }}
                  >
                    About
                  </Link>
                  <Link
                    to="/asset-finance"
                    className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                    onClick={(e) => {
                      if (window.location.pathname === '/asset-finance') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      handleNavClick('/asset-finance');
                    }}
                  >
                    Asset Finance
                  </Link>
                  <Link
                    to="/vehicle-finance"
                    className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                    onClick={(e) => {
                      if (window.location.pathname === '/vehicle-finance') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      handleNavClick('/vehicle-finance');
                    }}
                  >
                    Vehicle Finance
                  </Link>
                  <Link
                    to="/business-loan"
                    className="text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                    onClick={(e) => {
                      if (window.location.pathname === '/business-loan') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      handleNavClick('/business-loan');
                    }}
                  >
                    Business Loans
                  </Link>
                  <Link
                    to="/get-in-touch"
                    className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium text-center"
                    onClick={(e) => {
                      if (window.location.pathname === '/get-in-touch') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      handleNavClick('/get-in-touch');
                    }}
                  >
                    Get in Touch
                  </Link>
                </div>
              </nav>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1">
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/get-in-touch" element={<GetInTouch />} />
              <Route path="/asset-finance" element={<AssetFinance />} />
              <Route path="/vehicle-finance" element={<VehicleFinance />} />
              <Route path="/business-loan" element={<BusinessLoan />} />
              <Route path="/privacy-notice" element={<PrivacyNotice />} />
              <Route path="/complaints-procedure" element={<ComplaintsProcedure />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>

        {/* Footer */}
        <footer className="bg-gray-50 text-gray-700 border-t">
          <div className="container mx-auto px-4 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
              {/* Follow Us */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-gray-900">Follow Us</h3>
                <p className="text-sm text-gray-600 mb-6">Connect with us on social media for updates and finance tips.</p>
                <div className="flex gap-3 mb-6">
                  <a href="https://linkedin.com/company/finaways" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-800" aria-label="Visit our LinkedIn page">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Contact</h4>
                <p className="text-sm mb-3">Get in touch via phone, email, or live chat with one of our advisers.</p>
                <div className="space-y-2">
                  <a href="tel:01476602866" className="flex items-center text-blue-600 hover:text-blue-700 text-sm">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    01476 602866
                  </a>
                  <a href="mailto:info@finaways.co.uk" className="flex items-center text-blue-600 hover:text-blue-700 text-sm">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    Email Us
                  </a>
                </div>
              </div>

              {/* Our Company */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-gray-900">Our Company</h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/about"
                      className="text-gray-600 hover:text-blue-600"
                      onClick={(e) => {
                        if (window.location.pathname === '/about') {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      About Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/get-in-touch"
                      className="text-gray-600 hover:text-blue-600"
                      onClick={(e) => {
                        if (window.location.pathname === '/get-in-touch') {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy-notice"
                      className="text-gray-600 hover:text-blue-600"
                      onClick={(e) => {
                        if (window.location.pathname === '/privacy-notice') {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      Privacy Notice
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/complaints-procedure"
                      className="text-gray-600 hover:text-blue-600"
                      onClick={(e) => {
                        if (window.location.pathname === '/complaints-procedure') {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      Complaints Procedure
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Finance */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-gray-900">Finance</h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/asset-finance"
                      className="text-gray-600 hover:text-blue-600"
                      onClick={(e) => {
                        if (window.location.pathname === '/asset-finance') {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      Asset Finance
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/vehicle-finance"
                      className="text-gray-600 hover:text-blue-600"
                      onClick={(e) => {
                        if (window.location.pathname === '/vehicle-finance') {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      Vehicle Finance
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/business-loan"
                      className="text-gray-600 hover:text-blue-600"
                      onClick={(e) => {
                        if (window.location.pathname === '/business-loan') {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                    >
                      Business Loan
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-gray-900">Get in Touch</h3>
                <ul className="space-y-2">
                  <li><a href="tel:01476602866" className="text-gray-600 hover:text-blue-600">01476 602866</a></li>
                  <li><a href="mailto:info@finaways.co.uk" className="text-gray-600 hover:text-blue-600">info@finaways.co.uk</a></li>
                  <li className="text-gray-600">Autumn Park Business Centre</li>
                  <li className="text-gray-600">Dysart Rd, Grantham NG31 7EU</li>
                </ul>
              </div>
            </div>

            {/* Legal Text */}
            <div className="border-t pt-6 text-[10px] text-gray-600 space-y-2">
              <p>Finaways UK Limited is an Appointed Representative of Vanaways UK Limited, FRN: 1027619.</p>
              <p>Vanaways UK Limited is authorised and regulated by the Financial Conduct Authority, FRN: 940695.</p>
              <p>Vanaways UK Limited is an authorised credit broker and not a lender.</p>
              <p>We work with a Panel of Lenders whose particulars will be supplied upon request to find a potentially suitable arrangement for your consideration.</p>
              <p>We will receive commission from lenders. Different lenders pay different amounts depending on different commission models.</p>
              <p>For transparency we work with the following commission models: a percentage of the amount you borrow and rate for risk (this is based on the risk profile of the business).</p>
              <p>Further details of the commission model, calculation and amount will be disclosed to you throughout your customer journey.</p>
              <p>Finaways UK Limited (FRN:1027619) is a company registered in England and Wales (Company Number: 16232251) and is a credit broker, not a lender.</p>
              <p>Registered office: 68 Macrae Road, Eden Business Park, Bristol, BS20 0DD</p>
              <p>Trading Address: Autumn Park Business Centre, Dysart Rd, Grantham NG31 7EU.</p>
              <p>ICO registration number: ZB863748.</p>
            </div>

            {/* Copyright */}
            <div className="border-t mt-6 pt-6 text-center">
              <p className="text-sm text-gray-600">© 2025 Finaways UK Limited. All rights reserved</p>
            </div>
          </div>
        </footer>
        </div>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
